export type BuilderHistoryState<T> = {
  activeGroup: string | null;
  future: T[];
  limit: number;
  past: T[];
  present: T;
};

export type BuilderHistoryCommitResolution<T> = {
  controlledChanged: boolean;
  history: BuilderHistoryState<T>;
  historyChanged: boolean;
};

export type BuilderHistoryAction<T> =
  | {
      group: string;
      type: "cancel-group";
    }
  | {
      group?: string | null;
      type: "commit";
      value: T;
    }
  | {
      type: "end-group";
    }
  | {
      type: "redo";
    }
  | {
      type: "reset";
      value: T;
    }
  | {
      type: "synchronize";
      equals: (left: T, right: T) => boolean;
      value: T;
    }
  | {
      type: "undo";
    };

export function createBuilderHistory<T>(
  value: T,
  limit = 30,
): BuilderHistoryState<T> {
  return {
    activeGroup: null,
    future: [],
    limit: Math.max(1, limit),
    past: [],
    present: value,
  };
}

export function canUndoBuilderHistory<T>(
  history: BuilderHistoryState<T>,
): boolean {
  return history.past.length > 0;
}

export function canRedoBuilderHistory<T>(
  history: BuilderHistoryState<T>,
): boolean {
  return history.future.length > 0;
}

/**
 * Resolves a committed value against both sources of truth used by the
 * controlled builder:
 *
 * - `history.present` is the last committed value and therefore the undo
 *   baseline.
 * - `controlledValue` may already contain a live inspector preview.
 *
 * Keeping those comparisons separate prevents a previewed value from making
 * the final commit look like a no-op. Returning to the baseline is treated as
 * a controlled restoration without creating a phantom undo entry.
 */
export function resolveBuilderHistoryCommit<T>(
  history: BuilderHistoryState<T>,
  controlledValue: T,
  value: T,
  equals: (left: T, right: T) => boolean,
  group?: string | null,
): BuilderHistoryCommitResolution<T> {
  const historyChanged = !equals(history.present, value);
  const controlledChanged = !equals(controlledValue, value);

  return {
    controlledChanged,
    history: historyChanged
      ? builderHistoryReducer(history, {
          group,
          type: "commit",
          value,
        })
      : history,
    historyChanged,
  };
}

export function builderHistoryReducer<T>(
  history: BuilderHistoryState<T>,
  action: BuilderHistoryAction<T>,
): BuilderHistoryState<T> {
  switch (action.type) {
    case "cancel-group": {
      if (history.activeGroup !== action.group) {
        return history;
      }

      const base = history.past.at(-1);

      return base === undefined
        ? {
            ...history,
            activeGroup: null,
          }
        : {
            ...history,
            activeGroup: null,
            past: history.past.slice(0, -1),
            present: base,
          };
    }
    case "commit": {
      const group = action.group ?? null;

      if (group && history.activeGroup === group) {
        return {
          ...history,
          future: [],
          present: action.value,
        };
      }

      return {
        ...history,
        activeGroup: group,
        future: [],
        past: [...history.past, history.present].slice(-history.limit),
        present: action.value,
      };
    }
    case "end-group":
      return history.activeGroup
        ? {
            ...history,
            activeGroup: null,
          }
        : history;
    case "redo": {
      const [next, ...remainingFuture] = history.future;

      if (next === undefined) {
        return history;
      }

      return {
        ...history,
        activeGroup: null,
        future: remainingFuture,
        past: [...history.past, history.present].slice(-history.limit),
        present: next,
      };
    }
    case "reset":
      return createBuilderHistory(action.value, history.limit);
    case "synchronize":
      if (!action.equals(history.present, action.value)) {
        return createBuilderHistory(action.value, history.limit);
      }

      return history.present === action.value
        ? history
        : {
            ...history,
            present: action.value,
          };
    case "undo": {
      const previous = history.past.at(-1);

      if (previous === undefined) {
        return history;
      }

      return {
        ...history,
        activeGroup: null,
        future: [history.present, ...history.future].slice(0, history.limit),
        past: history.past.slice(0, -1),
        present: previous,
      };
    }
  }
}
